import { useState, useEffect } from 'react';
import { Coordinates, CalculationMethod, PrayerTimes, Prayer } from 'adhan';
import { Moon, Sun, Bell, BellOff, PlayCircle } from 'lucide-react';
import { useProfile } from '../hooks/useProfile';

interface NextPrayerCardProps {
  onPrayClick?: (prayer: string) => void;
}

export function NextPrayerCard({ onPrayClick }: NextPrayerCardProps) {
  const { profile, updateSettings } = useProfile();
  const [coords, setCoords] = useState<Coordinates | null>(null);
  const [nextPrayer, setNextPrayer] = useState<{ name: string, time: Date, countdown: string } | null>(null);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      setCoords(new Coordinates(pos.coords.latitude, pos.coords.longitude));
    });
  }, []);

  useEffect(() => {
    if (!coords) return;

    const update = () => {
      const params = CalculationMethod.MuslimWorldLeague();
      const now = new Date();
      const pt = new PrayerTimes(coords, now, params);
      
      let next = pt.nextPrayer();
      let nextTime = pt.timeForPrayer(next);
      let name = '';

      if (next === Prayer.None) {
        // Next is Fajr tomorrow
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const ptTomorrow = new PrayerTimes(coords, tomorrow, params);
        nextTime = ptTomorrow.fajr;
        name = 'Fajr';
      } else {
        switch(next) {
            case Prayer.Fajr: name = 'Fajr'; break;
            case Prayer.Sunrise: name = 'Sunrise'; break;
            case Prayer.Dhuhr: name = 'Dhuhr'; break;
            case Prayer.Asr: name = 'Asr'; break;
            case Prayer.Maghrib: name = 'Maghrib'; break;
            case Prayer.Isha: name = 'Isha'; break;
            default: name = 'Prayer';
        }
      }
      
      // Calculate Countdown
      if (nextTime) {
          const diff = nextTime.getTime() - now.getTime();
          const hrs = Math.floor(diff / (1000 * 60 * 60));
          const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          
          let countdown = '';
          if (hrs > 0) countdown = `${hrs}h ${mins}m`;
          else countdown = `${mins}m`;
          
          setNextPrayer({ name, time: nextTime, countdown });
      }
    };

    update();
    const timer = setInterval(update, 60000); // Update every minute
    return () => clearInterval(timer);
  }, [coords]);

  if (!nextPrayer) return null;

  const isNight = nextPrayer.name === 'Fajr' || nextPrayer.name === 'Maghrib' || nextPrayer.name === 'Isha';

  return (
    <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-2xl p-5 flex items-center justify-between shadow-lg shadow-emerald-900/10 border border-emerald-400/20 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute right-0 top-0 w-24 h-24 bg-white/10 rounded-full -mr-8 -mt-8 blur-2xl"></div>
      
      <div className="flex items-center gap-4 relative z-10">
        <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl border border-white/10">
           {isNight ? (
             <Moon className="w-6 h-6 text-emerald-50" />
           ) : (
             <Sun className="w-6 h-6 text-emerald-50" />
           )}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="text-xs font-medium text-emerald-100 uppercase tracking-wider mb-0.5">Next Prayer</p>
            <button 
              onClick={() => updateSettings({ prayerAlerts: !profile.settings.prayerAlerts })}
              className={`p-1.5 rounded-full transition-colors hover:bg-white/10 ${profile.settings.prayerAlerts ? 'text-white' : 'text-emerald-200/60'}`}
              title={profile.settings.prayerAlerts ? "Alerts On (15, 10, 5 min)" : "Alerts Off"}
            >
              {profile.settings.prayerAlerts ? <Bell className="w-3 h-3 fill-white" /> : <BellOff className="w-3 h-3" />}
            </button>
          </div>
          <h3 className="font-bold text-xl text-white mb-1">{nextPrayer.name}</h3>
          
          <button 
             onClick={() => onPrayClick?.(nextPrayer.name)}
             className="text-[10px] bg-white/20 hover:bg-white/30 active:scale-95 px-2.5 py-1.5 rounded-full flex items-center gap-1.5 transition-all font-semibold"
          >
            <PlayCircle className="w-3 h-3" />
            Pray {nextPrayer.name}
          </button>
        </div>
      </div>
      
      <div className="text-right relative z-10">
         <div className="flex items-baseline justify-end gap-1">
            <span className="text-3xl font-mono font-bold tracking-tight">{nextPrayer.countdown.split(' ')[0]}</span>
            {nextPrayer.countdown.includes(' ') && <span className="text-sm font-medium opacity-80">{nextPrayer.countdown.split(' ')[1]}</span>}
         </div>
         <p className="text-xs font-medium text-emerald-100 mt-1 bg-emerald-800/30 px-2 py-0.5 rounded-md inline-block">
            {nextPrayer.time.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
         </p>
      </div>
    </div>
  );
}